import { cn } from '@shared/lib/utils';
import { ComponentType, ReactNode } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  XAxisProps,
  YAxis,
  YAxisProps,
} from 'recharts';
import { DataKey, Margin } from 'recharts/types/util/types';
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '../ui/chart';
import { CustomTooltipProps } from './tooltips/types';

interface LineChartProps<T extends string, U extends string> {
  nameKey: T;
  dataKey: U;
  chartConfig: ChartConfig;
  chartData: any;
  legend?: boolean;
  className?: string;
  layout?: 'horizontal' | 'vertical';
  margin?: Margin;
  YAxisProps?: YAxisProps;
  XAxisProps?: XAxisProps;
  MaxBarSize?: number;
  barCategoryGap?: number | string;
  barSize?: number;
  tooltipFormatter?: ComponentType<CustomTooltipProps>;
  tooltipValueFormatter?: (value: number | string) => ReactNode;
  showGrid?: boolean;
  lineType?: 'monotone' | 'step' | 'stepBefore' | 'stepAfter' | 'linear';
  hideTooltipLabel?: boolean;
  referenceLines?: ReactNode;
  lineLabel?: (props: {
    x: number;
    y: number;
    value: number | string | undefined;
    index: number;
  }) => ReactNode;
}

export default function SimpleLineChart<
  T extends DataKey<string>,
  U extends DataKey<string[]>,
>({
  nameKey,
  dataKey,
  chartData,
  chartConfig,
  legend,
  className,
  margin = {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
  YAxisProps,
  XAxisProps,
  tooltipFormatter: Component,
  tooltipValueFormatter,
  showGrid = true,
  lineType = 'monotone',
  hideTooltipLabel = false,
  referenceLines,
  lineLabel,
}: LineChartProps<T, U>) {
  return (
    <ChartContainer
      config={chartConfig}
      className={(cn('aspect-auto h-full w-full'), className)}
    >
      <LineChart data={chartData} margin={margin}>
        {showGrid && <CartesianGrid vertical={false} />}
        <XAxis
          dataKey={nameKey}
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          {...XAxisProps}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tickFormatter={(value) => value}
          {...YAxisProps}
        />
        <ChartTooltip
          cursor={false}
          content={
            <ChartTooltipContent
              className={cn('w-60', {
                'w-fit': Component,
              })}
              hideLabel={hideTooltipLabel}
              indicator="dot"
              valueFormatter={tooltipValueFormatter}
              formatter={
                Component
                  ? (value, name, item, index) => (
                      <Component
                        value={value}
                        name={name}
                        item={item}
                        index={index}
                        chartConfig={chartConfig}
                      />
                    )
                  : undefined
              }
            />
          }
        />
        {legend && (
          <ChartLegend
            content={<ChartLegendContent className="text-md" />}
            verticalAlign="top"
          />
        )}
        {referenceLines}
        {Object.keys(chartConfig).map((key) => {
          return (
            <Line
              dataKey={key}
              key={key}
              type={lineType}
              fill={chartConfig[key].color}
              stroke={chartConfig[key].color}
              isAnimationActive={false}
              dot={false}
              label={lineLabel as any}
            />
          );
        })}
      </LineChart>
    </ChartContainer>
  );
}
